import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileBarChart, Filter, Download, RefreshCw, Search, X,
} from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { avReport, avReportExport } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { AV_STATUSES, statusLabel, CLAIM_TYPES } from "./avShared";
import AgeingBadge from "./AgeingBadge";

// Ageing levels — map 1:1 to the backend buckets: High=red, Medium=yellow, Low=green.
const AGEING_LEVELS = [
  { value: "ALL", label: "All" },
  { value: "high", label: "High (> 15 days)" },
  { value: "medium", label: "Medium (8–15 days)" },
  { value: "low", label: "Low (≤ 7 days)" },
];

const REPAIR_LABEL = {
  not_started: "Not Started",
  work_started: "Work Started",
  survey_pending: "Survey Pending",
  surveyor_not_assigned: "Surveyor Not Assigned",
  completed: "Completed",
};

const BILLING_LABEL = {
  pending: "Pending",
  partially_paid: "Partially Paid",
  paid: "Paid",
};

const money = (n) =>
  n == null ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const clampText = (s, n = 60) => {
  if (!s) return "";
  const t = String(s);
  return t.length > n ? t.slice(0, n - 1) + "…" : t;
};

const AvReports = () => {
  const { currentUser, locations, currentLocation } = useApp();
  const navigate = useNavigate();

  const allowedLocations = useMemo(
    () => locations.filter(
      (l) => currentUser?.role === "admin" || currentUser?.location_codes?.includes(l.code)
    ),
    [locations, currentUser]
  );

  const [q, setQ] = useState("");
  const [location, setLocation] = useState(
    currentLocation && currentLocation !== "ALL" ? currentLocation : "ALL"
  );
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [receivingFrom, setReceivingFrom] = useState("");
  const [receivingTo, setReceivingTo] = useState("");
  const [status, setStatus] = useState("ALL");
  const [claimType, setClaimType] = useState("ALL");
  const [model, setModel] = useState("");
  const [ageingLevel, setAgeingLevel] = useState("ALL");
  const [insuranceCompany, setInsuranceCompany] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const buildParams = () => {
    const p = {};
    if (q) p.q = q;
    if (location && location !== "ALL") p.location_code = location;
    if (fromDate) p.from_date = fromDate;
    if (toDate) p.to_date = toDate;
    if (receivingFrom) p.receiving_from = receivingFrom;
    if (receivingTo) p.receiving_to = receivingTo;
    if (status && status !== "ALL") p.status = status;
    if (claimType && claimType !== "ALL") p.claim_type = claimType;
    if (model) p.model = model;
    if (ageingLevel && ageingLevel !== "ALL") p.ageing_level = ageingLevel;
    if (insuranceCompany) p.insurance_company = insuranceCompany;
    return p;
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await avReport(buildParams());
      setRows(data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load AV report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    q, location, fromDate, toDate, receivingFrom, receivingTo, status, claimType, model, ageingLevel, insuranceCompany,
  ]);

  const handleExport = async () => {
    if (rows.length === 0) {
      toast.error("Nothing to export — adjust filters first.");
      return;
    }
    setExporting(true);
    try {
      const resp = await avReportExport(buildParams());
      const blob = new Blob([resp.data], { type: resp.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url;
      a.download = `av-report-${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} AV row(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => {
    setQ(""); setFromDate(""); setToDate(""); setReceivingFrom(""); setReceivingTo(""); setStatus("ALL");
    setClaimType("ALL"); setModel(""); setAgeingLevel("ALL"); setInsuranceCompany("");
  };

  const anyFilter =
    q || fromDate || toDate || receivingFrom || receivingTo || status !== "ALL" || claimType !== "ALL"
    || model || ageingLevel !== "ALL" || insuranceCompany;

  return (
    <div className="space-y-4" data-testid="av-reports-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg bg-yamaha-50 flex items-center justify-center">
          <FileBarChart className="w-6 h-6 text-yamaha-700" />
        </div>
        <div className="flex-1">
          <h1 className="text-[24px] font-semibold text-yamaha-800 leading-none tracking-tight">
            AV Reports
          </h1>
          <p className="text-[12.5px] text-[#747684] mt-1">
            Ageing-focused report across all Accidental Vehicles. Filter and export to Excel.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="av-report-refresh"
            onClick={load}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e2e1eb] text-[12px] text-[#444653] hover:bg-white"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            data-testid="av-report-export"
            disabled={exporting || rows.length === 0}
            onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-yamaha-700 hover:bg-yamaha-800 disabled:opacity-50 text-white text-[12px] font-semibold"
          >
            <Download className="w-3.5 h-3.5" />
            {exporting ? "Exporting…" : "Export to Excel"}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#e2e1eb] rounded-xl p-3">
        <div className="flex items-center gap-2 pb-2 mb-2 border-b border-[#eeecf6]">
          <Filter className="w-3.5 h-3.5 text-[#747684]" />
          <span className="text-[11.5px] font-semibold text-[#444653] uppercase tracking-wider">
            Filters
          </span>
          {anyFilter && (
            <button
              data-testid="av-report-reset-filters"
              onClick={resetFilters}
              className="ml-auto text-[11px] text-yamaha-700 hover:underline flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Reset
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
          {/* Search */}
          <div className="col-span-2 md:col-span-1 relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#747684]" />
            <input
              data-testid="av-report-q"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="AV ID / Customer / Reg / Mobile"
              className="w-full pl-8 pr-2 py-1.5 rounded-md border border-[#e2e1eb] text-[12px]"
            />
          </div>
          {/* Location */}
          <select
            data-testid="av-report-location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="px-2 py-1.5 rounded-md border border-[#e2e1eb] text-[12px] bg-white"
          >
            <option value="ALL">All Locations</option>
            {allowedLocations.map((l) => (
              <option key={l.code} value={l.code}>{l.name}</option>
            ))}
          </select>
          {/* Date From (Created) */}
          <input
            type="date"
            data-testid="av-report-from-date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            title="Created From"
            className="px-2 py-1.5 rounded-md border border-[#e2e1eb] text-[12px]"
          />
          {/* Date To (Created) */}
          <input
            type="date"
            data-testid="av-report-to-date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            title="Created To"
            className="px-2 py-1.5 rounded-md border border-[#e2e1eb] text-[12px]"
          />
          {/* Received On From */}
          <input
            type="date"
            data-testid="av-report-recv-from"
            value={receivingFrom}
            onChange={(e) => setReceivingFrom(e.target.value)}
            title="Received On From"
            placeholder="Received from"
            className="px-2 py-1.5 rounded-md border border-emerald-300 bg-emerald-50/40 text-[12px]"
          />
          {/* Received On To */}
          <input
            type="date"
            data-testid="av-report-recv-to"
            value={receivingTo}
            onChange={(e) => setReceivingTo(e.target.value)}
            title="Received On To"
            placeholder="Received to"
            className="px-2 py-1.5 rounded-md border border-emerald-300 bg-emerald-50/40 text-[12px]"
          />
          {/* Status */}
          <select
            data-testid="av-report-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-2 py-1.5 rounded-md border border-[#e2e1eb] text-[12px] bg-white"
          >
            <option value="ALL">All Statuses</option>
            {AV_STATUSES.filter((s) => s.value !== "draft").map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          {/* Claim Type */}
          <select
            data-testid="av-report-claim"
            value={claimType}
            onChange={(e) => setClaimType(e.target.value)}
            className="px-2 py-1.5 rounded-md border border-[#e2e1eb] text-[12px] bg-white"
          >
            <option value="ALL">All Claim Types</option>
            {CLAIM_TYPES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          {/* Model */}
          <input
            data-testid="av-report-model"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="Model contains…"
            className="px-2 py-1.5 rounded-md border border-[#e2e1eb] text-[12px]"
          />
          {/* Ageing Level */}
          <select
            data-testid="av-report-ageing"
            value={ageingLevel}
            onChange={(e) => setAgeingLevel(e.target.value)}
            className="px-2 py-1.5 rounded-md border border-[#e2e1eb] text-[12px] bg-white"
          >
            {AGEING_LEVELS.map((a) => (
              <option key={a.value} value={a.value}>{a.label}</option>
            ))}
          </select>
          {/* Insurance Company */}
          <input
            data-testid="av-report-insurance"
            value={insuranceCompany}
            onChange={(e) => setInsuranceCompany(e.target.value)}
            placeholder="Insurance company…"
            className="px-2 py-1.5 rounded-md border border-[#e2e1eb] text-[12px]"
          />
        </div>
      </div>

      {/* Result summary */}
      <div className="text-[12px] text-[#747684]">
        {loading ? "Loading…" : `${rows.length} record${rows.length === 1 ? "" : "s"} · sorted by ageing (high → low)`}
      </div>

      {/* Table */}
      <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden">
        <div className="overflow-auto max-h-[70vh]">
          <table className="w-full text-[11.5px]" data-testid="av-report-table">
            <thead className="bg-[#faf8ff] text-[10.5px] text-[#747684] uppercase tracking-wider sticky top-0 z-10">
              <tr>
                <th className="px-2 py-2 text-left whitespace-nowrap">Date</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">AV ID</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Ageing</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Customer</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Mobile</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Model</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Reg No</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Claim</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Insurance Co</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Status</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Hold Reason</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Received On</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Likely Del.</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Repair</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Billing</th>
                <th className="px-2 py-2 text-right whitespace-nowrap">Estimate</th>
                <th className="px-2 py-2 text-right whitespace-nowrap">Advance</th>
                <th className="px-2 py-2 text-right whitespace-nowrap">Cust. Payable</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Updated By</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Updated On</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">CRM Remarks</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">WM Remarks</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#eeecf6]">
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={22} className="px-3 py-10 text-center text-[12.5px] text-[#747684]">
                    No AV records match the current filters.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.id || r.av_id}
                  className="hover:bg-[#faf8ff] cursor-pointer"
                  onClick={() => r.id && navigate(`/av/${r.id}`)}
                  data-testid={`av-report-row-${r.av_id}`}
                >
                  <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap font-mono font-semibold text-yamaha-700">
                    {r.av_id}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <AgeingBadge days={r.ageing_days} bucket={r.ageing_bucket} />
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap max-w-[160px] truncate" title={r.customer_name}>
                    {r.customer_name}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap font-mono">{r.mobile_number}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap max-w-[140px] truncate" title={r.model}>
                    {r.model}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap font-mono">{r.registration_number}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {r.claim_type === "insured" ? "Insured" : "Non Insured"}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap max-w-[140px] truncate" title={r.insurance_company}>
                    {r.insurance_company || "—"}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{statusLabel(r.current_status)}</td>
                  <td className="px-2 py-1.5 max-w-[160px] truncate" title={r.hold_reason}>
                    {r.hold_reason || "—"}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {r.receiving_date ? fmtDate(r.receiving_date) : "—"}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {r.likely_delivery_date || "—"}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {REPAIR_LABEL[r.repair_status] || "—"}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {BILLING_LABEL[r.billing_status] || "—"}
                  </td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">{money(r.total_estimate_amount)}</td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">{money(r.total_advance)}</td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap font-semibold">
                    {money(r.customer_payable)}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap max-w-[140px] truncate" title={r.last_updated_by}>
                    {r.last_updated_by || "—"}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {r.last_updated_at ? fmtDateTime(r.last_updated_at) : "—"}
                  </td>
                  <td className="px-2 py-1.5 max-w-[200px] truncate" title={r.crm_remarks}>
                    {clampText(r.crm_remarks) || "—"}
                  </td>
                  <td className="px-2 py-1.5 max-w-[200px] truncate" title={r.wm_remarks}>
                    {clampText(r.wm_remarks) || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AvReports;
